import React, { SyntheticEvent } from "react";
import { useTranslation } from "utilities/i18n";
import { LoadingIndicator, PolyIcon, ToggleSwitchInput } from "elements";
import { Formik, FormikHelpers } from "collections";
import { Form, Input } from "collections/Form";
import "./PCAP.scss";
import { usePcapUploadLogic } from "./usePcapUploadLogic";
import { usePcapEnabledLogic } from "./usePcapEnabledLogic";
import { protect } from "TsRoleGuards";
import { needs, useGuard } from "rbac";
import { EnabledState, UploadState } from "./State";
import * as yup from "yup";
import { format } from "date-fns";
import { DATE_FORMAT } from "Constants";

export const PCAP: React.FC<{
  connected: boolean;
  handleRequestUploadToggle: (timeout: number) => Promise<void>;
  downloadUrl: string;
  uploadState: UploadState;
  handleEnabledChangeRequested: (event: SyntheticEvent) => Promise<void>;
  enabledState: EnabledState;
  hosed: boolean;
  stopTime: any;
}> = ({
  connected,
  handleRequestUploadToggle,
  downloadUrl,
  uploadState,
  handleEnabledChangeRequested,
  enabledState,
  hosed,
  stopTime
}) => {
  const { t } = useTranslation();

  const isUrlAvailable: boolean = Boolean(downloadUrl);

  let fileName = "unknown.pcap"; // default value, will be replaced with the real fileName when it comes in.

  if (isUrlAvailable) {
    const urlArray = downloadUrl.split("?")[0].split("/");
    fileName = urlArray[urlArray.length - 1];
  }

  const enabled = enabledState === EnabledState.Enabled;
  const enabling = enabledState === EnabledState.Enabling;
  const enablingOrDisabling =
    enabling || enabledState === EnabledState.Disabling;
  const idle = uploadState === UploadState.Idle;
  const uploading = uploadState === UploadState.Uploading;
  const stopping = uploadState === UploadState.Stopping;
  const loading = enabledState === EnabledState.Loading;

  const minUploadTimeout = 180;
  // 64800 => 18 hours
  // 65535 => Max Int, as defined in collections/Form/Input
  // 86400 => 24 hours, max allowed by device API
  const maxUploadTimeout = 64800;

  const RequestStartUploadFormSchema = yup.object({
    timeout: yup
      .number()
      .label("PCAP_TIMEOUT_VALIDATION")
      .min(minUploadTimeout, t("PCAP_TIMEOUT_MIN", { min: minUploadTimeout }))
      .max(maxUploadTimeout, t("PCAP_TIMEOUT_MAX", { max: maxUploadTimeout }))
      .integer()
      .required()
  });

  type RequestStartUploadForm = yup.InferType<
    typeof RequestStartUploadFormSchema
  >;

  const initialUploadFormValues: RequestStartUploadForm = { timeout: 180 };

  const handleUploadToggle = (
    values: RequestStartUploadForm,
    _helpers: FormikHelpers<RequestStartUploadForm>
  ) => {
    const coerced: number = +values.timeout;
    handleRequestUploadToggle(coerced);
  };

  return (
    <div className="pcap settings scrollable form grid">
      <div className="card section-title">
        <span className="title pl-1">{t("PCAP")}</span>
      </div>
      {loading && (
        <div>
          <p className="ml-15 mt-15 mb-0">{t("PCAP_LOADING_NOTE")}</p>
        </div>
      )}
      {!loading && (
        <>
          <div>
            <p className="ml-15 mt-15 mb-0">
              {enabled ? t("PCAP_ENABLED_NOTE") : t("PCAP_DISABLED_NOTE")}
            </p>
            <div className="form-group mb-0">
              <label className="col-3 text-right">{t("PCAP_ENABLE")}</label>
              <div className="col-5 link-group pb-5">
                <ToggleSwitchInput
                  checked={enabled || enabling}
                  onChange={handleEnabledChangeRequested}
                  disabled={hosed || enablingOrDisabling || !idle}
                  name="enabledToggle"
                />
              </div>
            </div>
            {enablingOrDisabling && (
              <div className="form-group mb-0">
                <div className="offset-3 col-5 link-group pb-5">
                  <p className={"mt-0 mb-15"}>
                    {enabling ? t("PCAP_ENABLING") : t("PCAP_DISABLING")}
                  </p>
                </div>
              </div>
            )}
            <p className={"ml-15 mt-15 mb-0" + (enabled ? "" : " opacity-50")}>
              {t("PCAP_MAX_NOTE")}
            </p>
            <p className={"ml-15 mt-0 mb-15" + (enabled ? "" : " opacity-50")}>
              {t("PCAP_STOP_NOTE")}
            </p>

            <Formik
              name="PCAPParams"
              initialValues={initialUploadFormValues}
              validationSchema={RequestStartUploadFormSchema}
              enableReinitialize={false}
              onSubmit={handleUploadToggle}
            >
              {({ isValid }) => (
                <Form className="form pb-2 pcap-params">
                  <div className="form-group mb-0">
                    <label className="col-3 text-right">
                      {t("PCAP_TIMEOUT")}
                    </label>
                    <div className="col-2">
                      <Input
                        name={"timeout"}
                        placeholder="180"
                        disabled={hosed || !idle || !enabled}
                        min={minUploadTimeout}
                        max={maxUploadTimeout}
                      />
                    </div>
                  </div>
                  <div className="form-group mb-0">
                    <div className="col-9 offset-3 mb-0">
                      {idle ? (
                        <button
                          className="btn btn-primary"
                          disabled={hosed || !enabled || !isValid}
                          type="submit"
                          name="submit"
                        >
                          {t("PCAP_START")}
                        </button>
                      ) : (
                        <button
                          className="btn btn-primary"
                          disabled={hosed || !uploading || !enabled}
                          type="submit"
                          name="submit"
                        >
                          <LoadingIndicator
                            className="inline sm white"
                            title={t("PCAP_STOP")}
                          />
                        </button>
                      )}
                      {!connected && (
                        <div className={"ml-0 mb-0 error-message has-error"}>
                          {t("PCAP_OFFLINE_TEXT")}
                        </div>
                      )}
                    </div>
                  </div>
                  {stopping && (
                    <div className="form-group mt-0 mb-0">
                      <div className="offset-3 col-5 link-group pb-5">
                        <p className={"mt-0 mb-0"}>{t("PCAP_STOPPING_NOTE")}</p>
                      </div>
                    </div>
                  )}
                  {uploading && stopTime && (
                    <div className="form-group mt-0 mb-0">
                      <p className="mt-1 col-6 offset-3 pcap-end">
                        {t("PCAP_END_TIME_MESSAGE")}
                        <br />
                        {format(new Date(stopTime), DATE_FORMAT.WITH_TIME_LONG)}
                      </p>
                    </div>
                  )}
                </Form>
              )}
            </Formik>
            {isUrlAvailable && (
              <>
                <div className="form-group">
                  <label className="col-3 text-right">
                    {t("PCAP_DOWNLOAD")}
                  </label>
                  <div className="col-5 link-group pb-5">
                    <a
                      href={downloadUrl}
                      className="ml-15 download-link"
                      download
                    >
                      {fileName}
                      <PolyIcon icon="download_arrow" className="pl-5" />
                    </a>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const PCAPLoader: React.FC<{
  deviceId: string;
  tenantId: string;
  deviceModel: string;
  siteId: string;
  connected: boolean;
  isPolyOs: boolean;
}> = ({ deviceId, tenantId, deviceModel, siteId, connected, isPolyOs }) => {
  const {
    handleRequestUploadToggle,
    downloadUrl,
    uploadState,
    stopTime
  } = usePcapUploadLogic(deviceId, 30000);

  const { handleEnabledChangeRequested, enabledState } = usePcapEnabledLogic(
    deviceId,
    tenantId,
    deviceModel,
    siteId,
    connected,
    100000,
    30000
  );

  const userCanMutate = useGuard(needs("upload:device:logs"));
  const hosed =
    enabledState === EnabledState.Failed || !connected || !userCanMutate;

  return (
    <PCAP
      connected={connected}
      handleRequestUploadToggle={handleRequestUploadToggle}
      downloadUrl={downloadUrl}
      uploadState={uploadState}
      handleEnabledChangeRequested={handleEnabledChangeRequested}
      enabledState={enabledState}
      hosed={hosed}
      stopTime={stopTime}
    />
  );
};

const ProtectedPCAP = protect("access:device:logs")(PCAPLoader);
export { ProtectedPCAP as PCAPLoader };
